import React, { useState } from 'react';
import { Upload, Button, notification, Progress, Modal } from '@ss/mtd-react';
import { useRootStore } from '@src/model';
import { observer } from 'mobx-react-lite';
import { analyzeResume, getPreview, getSchoolEnum, updateMetaData } from '@service/resumeDetail';
import { useRequest } from 'ahooks';
import { SOURCE_TYPE, LX_RESUME_EDIT_MODULE_ADD } from '@common/const';
import { LxSiteClick } from '@utils/siteLx';
import frontEndUpload, { attachmentSignUrl } from '@utils/uploadFromFE';
import { ResumeEducationExperience } from '@src/types/resumeDetail/campusType';
import ModuleView from '../../ModuleView';

import './index.scss';

const Helper = Upload.helper;

const ACCEPT_TYPES = ['.pdf', '.doc', '.docx'];

/**
 * 处理简历解析中教育经历scrollCode为null导致输入框学院名称查不到正确的值的问题
 * */
const normalizeEduSchool = async (resumeEduExpList: ResumeEducationExperience[]) => {
  // 通过schoolName发起请求拿到code值，会有多段教育经历 是个数组
  const querySchoolCodeByName = resumeEduExpList.map((i) => getSchoolEnum({ filter: i.school }));
  const schoolList = await Promise.allSettled(querySchoolCodeByName);
  const newResumeEduExpList = resumeEduExpList.map((eduExp) => {
    // 根据解析的schoolName去查询搜索学校code，返回的是学校列表数组
    const matchedSchool = schoolList?.find(
      (school) => school.status === 'fulfilled' && school.value?.some((i) => i.name === eduExp.school),
    );
    if (matchedSchool && matchedSchool.status === 'fulfilled') {
      const schoolInfo = matchedSchool.value?.find((i) => i.name === eduExp.school && i.id); // 这里会有同名的情况 取id和name都有值的学校
      if (schoolInfo) {
        return { ...eduExp, schoolCode: schoolInfo.id };
      }
    }
    // 如果没有找到匹配的学校信息，返回原对象
    return eduExp;
  });
  return newResumeEduExpList;
};

function UploadResume(props) {
  const { type, data } = props;
  const { campusResumeDetail, socialResumeDetail } = useRootStore();
  const [loadingText, setLoadingText] = useState('上传中');

  let store;
  if (type === 'campus') {
    store = campusResumeDetail;
  }
  if (type === 'social') {
    store = socialResumeDetail;
  }

  const [loading, setLoading] = useState(false);
  const [percentage, setPercentage] = useState(0);

  const { run } = useRequest(analyzeResume, {
    manual: true,
    onBefore: () => {
      setLoadingText('解析中');
    },
    onSuccess: async (res) => {
      notification.success({
        title: '成功',
        message: '解析简历成功',
      });

      // 解析的简历手机号和resumeUnionId不能覆盖以前的手机号
      const { resumeVo } = res || {};
      const { resumeBaseInfo, resumeEduExpList } = resumeVo || {};
      const newResumeEduExpList = await normalizeEduSchool(resumeEduExpList ?? []);
      // 使用处理后的结果进行覆盖
      resumeVo.resumeEduExpList = newResumeEduExpList;
      resumeBaseInfo.interCode = store.resumeBaseInfo.interCode;
      resumeBaseInfo.interCodeName = store.resumeBaseInfo.interCodeName;
      resumeBaseInfo.phone = store.resumeBaseInfo.phone;
      store.setStoreData(res);
      // 如果不存在教育经历，需要添加一段空白的教育经历
      if (!resumeVo?.resumeEduExpList?.length) {
        store.addResumeList('resumeEduExpList');
      }
    },
    onFinally: () => {
      setLoading(false);
      setLoadingText('上传中');
    },
  });

  /**
   * 上传附件前回调
   */
  const onBeforeUpload = (file: any) => {
    LxSiteClick({ bid: LX_RESUME_EDIT_MODULE_ADD.uploadResume, valLab: { resumeType: type } });
    const fileName = file.name.split('.');
    const fileType = `.${fileName[fileName.length - 1]}`;
    if (!ACCEPT_TYPES.includes(fileType)) {
      notification.error({
        title: '错误',
        message: '不支持上传该文件类型的文件！',
      });
      return false;
    }

    if (Helper.getFileSizeWithMB(file) > 10) {
      notification.error({
        title: '错误',
        message: '附件简历大小限制为10M，请重新上传',
      });
      return false;
    }
    return true;
  };

  /**
   * 上传附件失败
   */
  const onError = (error: string) => {
    console.log('上传失败, error:', error);
    notification.error({
      title: '错误',
      message: '上传失败, 请切换网络后刷新页面重试',
      // message: error || '上传简历失败',
    });
  };

  const handleUpload = (btnTypes: string, file?: File[]) => {
    if (!file?.[0]) return false;

    frontEndUpload({
      signUrl: `${attachmentSignUrl}?fileTypeCode=4`,
      accept: ACCEPT_TYPES,
      file: file[0],
      onValidateFile: onBeforeUpload,
      onError,
      onStart: () => {
        setLoading(true);
        setPercentage(0);
      },
      onProgress: (percent) => {
        setPercentage(percent);
      },
      onFileInfo: async (fileInfo) => {
        const { key, fileName, fileType, fileSize } = fileInfo;
        // 先通知后端更新元数据
        await updateMetaData({ fileId: key, fileName, fileSize, fileTypeCode: '4' });

        const param = {
          fileId: key,
          name: fileName,
          type: fileType?.split('/')[1],
          unionId: '',
          fileTypeCode: '4',
        };
        if (store.resumeAttachmentList?.length) store.deleteResumeList('resumeAttachmentList', 0);
        store.addResumeList('resumeAttachmentList', param);
        if (btnTypes === 'onOk') {
          run({ fileId: key, source: type === 'campus' ? SOURCE_TYPE.campus : SOURCE_TYPE.social });
        } else {
          setLoading(false);
          notification.success({
            title: '成功',
            message: '上传简历附件成功',
          });
        }
      },
    });

    // 始终阻止upload组件上传
    return false;
  };

  /**
   * 上传简历附件
   */
  const onUpload = (file?: File[]) => {
    Modal.confirm({
      className: 'common_modal',
      style: { width: 430, minHeight: 196 },
      closable: false,
      maskClosable: false,
      message: '是否需要对简历附件进行解析并填充下方简历内容？',
      // cancelBtnProps: { size: 'large', width:'143px' },
      okBtnProps: { type: 'warning' },
      okText: '是，需同步更新简历内容',
      cancelText: '否，仅做附件上传',
      onOk: async () => {
        handleUpload('onOk', file);
      },
      onCancel: async () => {
        handleUpload('onCancel', file);
      },
    });
  };

  // 简历预览
  const handlePreview = async (fileId: string) => {
    const res = await getPreview(fileId);
    // 先请求接口再新打开窗口，不然接口报错打开的是个空白页
    const newWindow = window.open();
    if (newWindow) {
      newWindow.location = res;
    } else {
      notification.error({
        message: '抱歉，当前浏览器环境不支持预览，请使用Chrome浏览器重试。',
      });
    }
  };

  // 删除简历
  const handleDeleteResume = () => {
    store.deleteResumeList('resumeAttachmentList', 0);
  };

  return (
    <div className="upload_resume_edit model_edit">
      <Upload
        className="draggable-upload"
        draggable
        onBeforeUpload={onUpload}
        multiple={false}
        accept=".pdf,.doc,.docx"
      >
        {loading && (
          <>
            <div className="info_1">{loadingText}</div>{' '}
            <div className="progress-box">
              <Progress className="progress-base" percentage={percentage} color="#FFD000" />
            </div>
          </>
        )}
        {!loading && (
          <>
            <div
              className="info_1"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {!data?.length ? '点击或将简历附件拖拽到这里上传' : data?.[0].name}
            </div>
            {!data?.length && (
              <div className="upload_button">
                <Button>上传简历附件</Button>
              </div>
            )}
            {!!data?.length && (
              <div className="info_2">
                <span
                  className="preview"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreview(data[0].fileId);
                    }}
                  >
                    预览
                  </span>
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  ｜
                </span>
                <span
                  className="delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteResume();
                  }}
                >
                  删除
                </span>
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  {' '}
                  ｜
                </span>
                <span className="reload">
                  <span>重新上传</span>
                  <span
                    className="none"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {' '}
                  </span>
                </span>
              </div>
            )}

            <div
              className="info_3"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              支持doc、docx、pdf格式（10M以下）
            </div>
          </>
        )}
      </Upload>
    </div>
  );
}

export default ModuleView(observer(UploadResume), 'uploadResume');
